import React, { useEffect, useRef } from 'react';

import { PieChart, PieSeriesOption } from 'echarts/charts';
import { TooltipComponent, LegendComponent, ToolboxComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useIntl } from 'react-intl';

import { CommonChartProps, formatValue } from './chartsCommons';
import { chartColors } from '../../configs/chart.config';

echarts.use([PieChart, TooltipComponent, LegendComponent, ToolboxComponent, CanvasRenderer]);

type EChartsOption = echarts.ComposeOption<PieSeriesOption>;

export type EChartsPieChartProps<T> = CommonChartProps<T> & {
  centralValue: string;
  centralLabel?: string;
  width?: number | string;
  height?: number | string;
  mode?: 'card' | 'modal';
  visibleLegend?: boolean;
};

export function EChartsPieChart<T>({
  data,
  keyField,
  valueField,
  formatKey,
  valueFormatOptions,
  centralValue,
  centralLabel,
  width = '100%',
  height = '480px',
  mode = 'card',
  visibleLegend = true,
}: EChartsPieChartProps<T>) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const intl = useIntl();

  const pieData = data.map((item) => {
    const name = typeof keyField === 'function' ? keyField(item) : String((item as any)[keyField]);

    const getValue = (item: T) => {
      if (!valueField) return 0;

      if (typeof valueField === 'function') {
        return valueField(item);
      }

      const key = valueField as keyof T;
      return Number((item as Record<keyof T, unknown>)[key]);
    };
    return {
      name: formatKey ? formatKey(name) : name,
      value: getValue(item) ?? 0,
      itemStyle: (item as any).itemStyle,
    };
  });

  const showLegend = parseInt(height, 10) >= 480 && visibleLegend;

  const centralValueMinText =
    centralValue.length < 7
      ? 20
      : centralValue.length < 10
        ? 18
        : centralValue.length < 12
          ? 12
          : 10;
  const centralValueMaxText = centralValue.length < 12 ? 26 : centralValue.length < 14 ? 24 : 20;

  const option: EChartsOption = {
    color: chartColors.colorPalette,
    tooltip: {
      backgroundColor: 'rgba(66,66,66,0.95)',
      borderWidth: 0,
      trigger: 'item',
      formatter: (params: any) => {
        const name = params.name;
        const value = formatValue(intl, params.value, valueFormatOptions);
        return `   <div style="padding: 4px 8px; max-width: 300px;">
                  <p style="margin: 0; font-weight: bold; font-size: 12px; color: white;">
                    ${name}
                  </p>
                  <p style="margin: 0; font-size: 12px; color: ${params.color};">
                    ${value}
                  </p>
                </div>`;
      },
    },
    legend: {
      show: showLegend,
      orient: 'vertical',
      bottom: 0,
      right: 5,
      textStyle: {
        color: '#9E9E9E',
      },
    },
    series: [
      {
        name: centralLabel ?? '',
        type: 'pie',
        roseType: 'area',
        radius: ['30%', '80%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: ' #1F1F1F',
          borderWidth: 1,
        },
        label: {
          show: true,
          position: 'outside',
          formatter: (params: any) => {
            const label = params.name || '';
            const maxLength = mode === 'card' ? 15 : 40;
            return label.length > maxLength ? label.slice(0, maxLength) + '…' : label;
          },
          color: '#9E9E9E',
        },
        labelLine: {
          show: true,
        },
        data: pieData,
      },
    ],
    graphic: [
      {
        type: 'group',
        left: 'center',
        top: 'center',
        children: [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: centralValue,
              fontSize: mode !== 'card' ? centralValueMaxText : centralValueMinText,
              fontWeight: 700,
              fill: '#3d53f5',
              textAlign: 'center',
            },
          },
          centralLabel
            ? {
                type: 'text',
                left: 'center',
                top: mode === 'card' ? centralValueMinText : 16,
                style: {
                  text: centralLabel,
                  fontSize: 14,
                  fontWeight: 700,
                  fill: '#3d53f5',
                  textAlign: 'center',
                },
              }
            : {},
        ],
      },
    ],
  };

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }
      chartInstance.current.setOption(option);
    }
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [option]);

  return (
    <div
      ref={chartRef}
      style={{
        width,
        height,
      }}
    />
  );
}
